import api from "./api";

interface AudioQueueItem {
  id: string;
  text: string;
  language: string;
  timestamp: number;
}

class AudioQueueManager {
  private queue: AudioQueueItem[] = [];
  private isPlaying: boolean = false;
  private currentAudio: HTMLAudioElement | null = null;
  private currentPlayingText: string = "";
  private recentPlayedTexts: Set<string> = new Set();

  // 큐에 오디오 아이템 추가
  public enqueue(text: string, language: string = "en"): void {
    // 영어가 아닌 경우 무시
    if (language !== "en" && language !== "en-US") {
      return;
    }

    const item: AudioQueueItem = {
      id: `audio-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text: text.trim(),
      language,
      timestamp: Date.now(),
    };

    // 빈 텍스트는 추가하지 않음
    if (!item.text) {
      return;
    }

    const normalizedText = item.text.toLowerCase().trim();

    // 현재 재생 중인 텍스트와 동일한 경우 무시
    if (this.currentPlayingText === normalizedText) {
      console.log(
        "현재 재생 중인 텍스트와 동일하여 TTS 큐에 추가하지 않음:",
        item.text
      );
      return;
    }

    // 최근 재생된 텍스트와 동일한 경우 무시 (지난 30초 내)
    if (this.recentPlayedTexts.has(normalizedText)) {
      console.log(
        "최근 재생된 텍스트와 동일하여 TTS 큐에 추가하지 않음:",
        item.text
      );
      return;
    }

    // 큐에 이미 동일한 텍스트가 있는 경우 무시
    const isDuplicateInQueue = this.queue.some(
      (queueItem) => queueItem.text.toLowerCase().trim() === normalizedText
    );

    if (isDuplicateInQueue) {
      console.log(
        "큐에 이미 동일한 텍스트가 있어 TTS 큐에 추가하지 않음:",
        item.text
      );
      return;
    }

    this.queue.push(item);
    console.log("TTS 큐에 추가됨:", item.text);

    // 현재 재생 중이 아니면 재생 시작
    if (!this.isPlaying) {
      this.processQueue();
    }
  }

  // 큐 처리 (순차적으로 오디오 재생)
  private async processQueue(): Promise<void> {
    if (this.queue.length === 0 || this.isPlaying) {
      return;
    }

    this.isPlaying = true;

    while (this.queue.length > 0) {
      const item = this.queue.shift();
      if (!item) break;

      try {
        console.log("TTS 재생 시작:", item.text);
        await this.playAudio(item);
        console.log("TTS 재생 완료:", item.text);
      } catch (error) {
        console.error("TTS 재생 오류:", error, "Text:", item.text);
      }

      // 각 오디오 사이에 짧은 간격 추가
      await this.delay(300);
    }

    this.isPlaying = false;
  }

  // 개별 오디오 재생
  private async playAudio(item: AudioQueueItem): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        // 현재 재생 중인 텍스트 설정
        const normalizedText = item.text.toLowerCase().trim();
        this.currentPlayingText = normalizedText;
        // 서버에 TTS 요청
        const response = await api.post(
          "/classes/tts",
          { text: item.text },
          {
            responseType: "arraybuffer",
            headers: {
              Accept: "audio/mpeg",
            },
          }
        );

        // ArrayBuffer를 Blob으로 변환
        const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Audio 객체 생성 및 재생
        const audio = new Audio(audioUrl);
        this.currentAudio = audio;

        // 오디오 이벤트 리스너 설정
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;

          // 재생 완료된 텍스트를 최근 재생 목록에 추가
          this.recentPlayedTexts.add(normalizedText);

          // 30초 후에 최근 재생 목록에서 제거
          setTimeout(() => {
            this.recentPlayedTexts.delete(normalizedText);
          }, 30000);

          // 현재 재생 중인 텍스트 초기화
          this.currentPlayingText = "";

          resolve();
        };

        audio.onerror = (error) => {
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;

          // 현재 재생 중인 텍스트 초기화
          this.currentPlayingText = "";

          reject(new Error(`오디오 재생 실패: ${error}`));
        };

        // 볼륨 설정 (70%)
        audio.volume = 0.7;

        // 재생 시작
        await audio.play();
      } catch (error) {
        reject(error);
      }
    });
  }

  // 지연 함수
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // 현재 재생 중인 오디오 정지
  public stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    this.isPlaying = false;
    this.currentPlayingText = "";
  }

  // 큐 클리어
  public clear(): void {
    this.stop();
    this.queue = [];
    this.recentPlayedTexts.clear();
    console.log("TTS 큐가 클리어되었습니다.");
  }

  // 큐 상태 확인
  public getQueueStatus(): { queueLength: number; isPlaying: boolean } {
    return {
      queueLength: this.queue.length,
      isPlaying: this.isPlaying,
    };
  }

  // 큐에서 오래된 항목 제거 (메모리 관리)
  public cleanupOldItems(maxAge: number = 60000): void {
    const now = Date.now();
    this.queue = this.queue.filter((item) => now - item.timestamp < maxAge);
  }
}

// 싱글톤 인스턴스 생성
const audioQueueManager = new AudioQueueManager();

export default audioQueueManager;
